<script>
  import { onMount } from "svelte";
  import { apiRequest } from "../utils/api";
  import { formatDate } from "../utils/format.js";

  let tokens = [];
  let loadingTokens = false;
  let loadingError = null;
  let createError = null;
  let creating = false;
  let revokingId = null;
  let tokenForm = {
    name: "",
    expiresAt: "",
    allowedIps: ""
  };
  let createdToken = null;
  let copySuccess = false;

  // Shown in the connection help so the endpoint matches whatever host is serving
  // the console (localhost in dev, the deployed origin otherwise).
  let mcpEndpoint = "/mcp/rpc";

  onMount(() => {
    if (typeof window !== "undefined") {
      mcpEndpoint = `${window.location.origin}/mcp/rpc`;
    }
    loadTokens();
  });

  function parseAllowedIps(rawText) {
    return rawText
      .split(/[\n,]/)
      .map((value) => value.trim())
      .filter(Boolean);
  }

  function toIsoDateTime(value) {
    if (!value) {
      return null;
    }

    return new Date(value).toISOString();
  }

  function resetCreateForm() {
    tokenForm = {
      name: "",
      expiresAt: "",
      allowedIps: ""
    };
  }

  async function loadTokens() {
    loadingTokens = true;
    loadingError = null;

    try {
      tokens = await apiRequest("/api/personal_access_tokens");
    } catch (error) {
      loadingError = error?.message || "Could not load tokens.";
      tokens = [];
    } finally {
      loadingTokens = false;
    }
  }

  async function createToken(event) {
    event?.preventDefault();
    if (!tokenForm.name.trim() || creating) {
      return;
    }

    createError = null;
    creating = true;
    copySuccess = false;

    try {
      const response = await apiRequest("/api/personal_access_tokens", {
        method: "POST",
        body: {
          personal_access_token: {
            name: tokenForm.name.trim(),
            expires_at: toIsoDateTime(tokenForm.expiresAt) || null,
            allowed_ips: parseAllowedIps(tokenForm.allowedIps)
          }
        }
      });

      createdToken = response;
      resetCreateForm();
      await loadTokens();
    } catch (error) {
      createError = error?.message || "Could not create token.";
    } finally {
      creating = false;
    }
  }

  async function revokeToken(token) {
    revokingId = token.id;

    try {
      await apiRequest(`/api/personal_access_tokens/${token.id}`, { method: "DELETE" });
      tokens = tokens.map((entry) =>
        entry.id === token.id
          ? { ...entry, revoked_at: new Date().toISOString(), last_used_at: entry.last_used_at }
          : entry
      );
    } catch (error) {
      createError = error?.message || "Could not revoke token.";
    } finally {
      revokingId = null;
    }
  }

  async function copyToken() {
    if (!createdToken?.token) {
      return;
    }

    try {
      await navigator.clipboard.writeText(createdToken.token);
    } catch (_error) {
      const temp = document.createElement("textarea");
      temp.value = createdToken.token;
      document.body.appendChild(temp);
      temp.select();
      document.execCommand("copy");
      document.body.removeChild(temp);
    }

    copySuccess = true;
    setTimeout(() => (copySuccess = false), 1600);
  }
</script>

<h2 class="text-base font-bold text-neutral-800 mb-4">Personal Access Tokens</h2>

{#if createdToken}
  <article class="bg-warning-bg border border-warning-border/40 rounded-xl p-4 mb-5 shadow-xs flex gap-3.5" role="status" aria-live="polite">
    <div class="text-warning-text mt-0.5 shrink-0">
      <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
      </svg>
    </div>
    <div class="space-y-2 flex-1 min-w-0">
      <div>
        <h3 class="text-xs font-bold text-warning-text uppercase tracking-wider mb-0.5">New personal access token created</h3>
        <p class="text-xs text-neutral-600">
          Make sure to copy your personal access token now. <span class="font-bold text-warning-text">You won't be able to see it again!</span>
        </p>
      </div>
      <div class="relative group">
        <pre class="font-mono text-[0.78rem] text-neutral-800 bg-white border border-neutral-200 rounded-lg p-3 break-all select-all font-semibold shadow-2xs pr-12">{createdToken.token}</pre>
        <button type="button" class="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-neutral-50 text-neutral-500 hover:text-neutral-700 transition-colors" title="Copy to clipboard" on:click={copyToken}>
          {#if copySuccess}
            <svg class="w-4 h-4 text-success-text" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
            </svg>
          {:else}
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0A2.25 2.25 0 0 1 13.5 5.25h-3a2.25 2.25 0 0 1-2.166-1.638m7.332 0t-7.332 0M15.666 3.888m0 0A2.25 2.25 0 0 0 12 5.25c.97 0 1.792-.62 2.086-1.488M8.25 21H12m0 0H15.666m-7.332 0c-1.03 0-1.9-.693-2.166-1.638m7.332 0c.97 0 1.792-.62 2.086-1.488m-9.418 3.126V5.25c0-.621.504-1.125 1.125-1.125h.375m10.125 16.5V5.25c0-.621-.504-1.125-1.125-1.125h-.375" />
            </svg>
          {/if}
        </button>
      </div>
      {#if copySuccess}
        <span class="inline-flex text-[0.68rem] font-bold text-success-text px-2 py-0.5 rounded-full bg-success-bg border border-success-border/20">Copied to clipboard!</span>
      {/if}
    </div>
  </article>
{/if}

<form class="bg-neutral-50 border border-neutral-200 rounded-lg p-4 mb-5" on:submit={createToken}>
  <h3 class="text-xs font-bold text-neutral-800 uppercase tracking-wider mb-3">Create Token</h3>
  
  <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
    <div class="form-group">
      <label class="form-label" for="tok-name">Name</label>
      <input
        id="tok-name"
        class="form-input"
        required
        value={tokenForm.name}
        placeholder="e.g. CI service"
        on:input={(event) => (tokenForm.name = event.target.value)}
      />
    </div>

    <div class="form-group">
      <label class="form-label" for="tok-expiry">Expires at (optional)</label>
      <input
        id="tok-expiry"
        class="form-input"
        type="datetime-local"
        value={tokenForm.expiresAt}
        on:input={(event) => (tokenForm.expiresAt = event.target.value)}
      />
    </div>
  </div>

  <div class="form-group mb-3">
    <label class="form-label" for="tok-ips">Allowed IPs (optional, comma/newline separated exact IPs)</label>
    <textarea
      id="tok-ips"
      class="form-input font-mono"
      value={tokenForm.allowedIps}
      rows="3"
      placeholder={"127.0.0.1\n10.0.0.1"}
      on:input={(event) => (tokenForm.allowedIps = event.target.value)}
    ></textarea>
  </div>

  <div class="flex justify-end">
    <button type="submit" class="btn-primary" disabled={creating}>{creating ? "Creating..." : "Create token"}</button>
  </div>
</form>

{#if createError}
  <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-3 py-1.5 text-xs mb-4" role="alert">{createError}</p>
{/if}

<h3 class="text-xs font-bold text-neutral-700 uppercase tracking-wider mt-5 mb-3">Current tokens</h3>

{#if loadingTokens}
  <div class="space-y-3 animate-pulse">
    {#each Array(2) as _}
      <div class="bg-white border border-neutral-200 rounded-lg p-3.5 flex justify-between gap-3 shadow-2xs">
        <div class="space-y-2 flex-1">
          <div class="flex items-center gap-2">
            <div class="h-4 bg-neutral-200 rounded-sm w-32"></div>
            <div class="h-4 bg-neutral-200 rounded-full w-14"></div>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div class="h-3 bg-neutral-200 rounded-sm w-3/4"></div>
            <div class="h-3 bg-neutral-200 rounded-sm w-2/3"></div>
          </div>
        </div>
        <div class="h-8 bg-neutral-200 rounded-md w-16"></div>
      </div>
    {/each}
  </div>
{:else if loadingError}
  <div class="bg-white border border-danger-border/20 rounded-xl p-6 text-center space-y-3 shadow-sm" role="alert">
    <div class="mx-auto w-10 h-10 rounded-full bg-danger-bg flex items-center justify-center text-danger-text">
      <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
      </svg>
    </div>
    <div class="space-y-1">
      <p class="text-xs font-bold text-neutral-900">Failed to load active tokens</p>
      <p class="text-[0.7rem] text-neutral-500 max-w-xs mx-auto">{loadingError}</p>
    </div>
    <button type="button" class="btn-secondary py-0.5 px-2 text-[0.78rem]" on:click={loadTokens}>
      Retry loading
    </button>
  </div>
{:else if tokens.length === 0}
  <div class="bg-white border border-neutral-200 rounded-xl p-8 text-center space-y-3 shadow-xs">
    <div class="mx-auto w-12 h-12 rounded-full bg-brand-50 flex items-center justify-center text-brand-600">
      <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
      </svg>
    </div>
    <div class="space-y-1">
      <h4 class="text-xs font-semibold text-neutral-900">No active tokens yet</h4>
      <p class="text-[0.7rem] text-neutral-500 max-w-xs mx-auto">Create a personal access token above to allow MCP clients and AI agents to create and verify report rows.</p>
    </div>
  </div>
{:else}
  <ul class="space-y-3" aria-live="polite">
    {#each tokens as token}
      <li class="bg-white border border-neutral-200 rounded-lg p-3.5 flex flex-col sm:flex-row justify-between gap-3 shadow-2xs transition-colors hover:border-neutral-300">
        <div class="space-y-1">
          <div class="flex items-center gap-2">
            <strong class="text-sm font-semibold text-neutral-900">{token.name}</strong>
            {#if token.revoked_at}
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[0.68rem] font-bold uppercase tracking-wider bg-warning-bg text-warning-text border border-warning-border/20">Revoked</span>
            {:else}
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[0.68rem] font-bold uppercase tracking-wider bg-success-bg text-success-text border border-success-border/20">Active</span>
            {/if}
          </div>
          
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs text-neutral-500">
            <p><span class="font-medium text-neutral-700">Created:</span> {formatDate(token.created_at)}</p>
            <p><span class="font-medium text-neutral-700">Expires:</span> {formatDate(token.expires_at)}</p>
            <p><span class="font-medium text-neutral-700">Last used:</span> {formatDate(token.last_used_at)}</p>
            <p class="sm:col-span-2 md:col-span-1"><span class="font-medium text-neutral-700">Allowed IPs:</span> {(token.allowed_ips || []).join(", ") || "Any"}</p>
          </div>
        </div>

        {#if !token.revoked_at}
          <div class="flex items-start justify-end shrink-0">
            <button
              type="button"
              class="btn-danger py-1 px-3 text-xs w-full sm:w-auto"
              disabled={revokingId === token.id}
              on:click={() => revokeToken(token)}
            >
              {revokingId === token.id ? "Revoking..." : "Revoke"}
            </button>
          </div>
        {/if}
      </li>
    {/each}
  </ul>
{/if}

<section class="border-t border-neutral-200 pt-4 mt-8 space-y-4">
  <div class="space-y-1">
    <h3 class="text-xs font-bold text-neutral-800 tracking-wider">Connecting an MCP client</h3>
    <p class="text-[0.72rem] text-neutral-500 leading-relaxed">
      This app exposes a Streamable-HTTP MCP server. Point any MCP client at the endpoint below
      and authenticate with a personal access token as a <code class="font-mono">Bearer</code> token.
      Create a token above, then drop it into your client config in place of <code class="font-mono">&lt;YOUR_TOKEN&gt;</code>.
    </p>
  </div>

  <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-[0.72rem]">
    <div class="flex gap-2">
      <dt class="font-medium text-neutral-700 shrink-0">Endpoint:</dt>
      <dd class="font-mono text-neutral-800 break-all">{mcpEndpoint}</dd>
    </div>
    <div class="flex gap-2">
      <dt class="font-medium text-neutral-700 shrink-0">Auth header:</dt>
      <dd class="font-mono text-neutral-800 break-all">Authorization: Bearer &lt;YOUR_TOKEN&gt;</dd>
    </div>
  </dl>

  <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
    <div class="space-y-1.5">
      <p class="text-[0.72rem] font-bold text-neutral-700">Claude Code</p>
      <p class="text-[0.68rem] text-neutral-500 leading-relaxed">
        Add the server over HTTP with the token as a header (or run
        <code class="font-mono">claude mcp add</code>). Use <code class="font-mono">--scope project</code>
        to write it into a shared <code class="font-mono">.mcp.json</code>:
      </p>
      <pre class="font-mono text-[0.68rem] text-neutral-800 bg-neutral-50 border border-neutral-200 rounded-lg p-3 overflow-x-auto whitespace-pre">{`claude mcp add --transport http report-generator \\
  ${mcpEndpoint} \\
  --header "Authorization: Bearer <YOUR_TOKEN>"`}</pre>
      <p class="text-[0.68rem] text-neutral-500 leading-relaxed">
        Or write it directly into <code class="font-mono">.mcp.json</code>:
      </p>
      <pre class="font-mono text-[0.68rem] text-neutral-800 bg-neutral-50 border border-neutral-200 rounded-lg p-3 overflow-x-auto whitespace-pre">{`{
  "mcpServers": {
    "report-generator": {
      "type": "http",
      "url": "${mcpEndpoint}",
      "headers": { "Authorization": "Bearer <YOUR_TOKEN>" }
    }
  }
}`}</pre>
    </div>

    <div class="space-y-1.5">
      <p class="text-[0.72rem] font-bold text-neutral-700">Codex</p>
      <p class="text-[0.68rem] text-neutral-500 leading-relaxed">
        Add the server to <code class="font-mono">~/.codex/config.toml</code> (or run
        <code class="font-mono">codex mcp add</code>). Codex speaks HTTP MCP natively:
      </p>
      <pre class="font-mono text-[0.68rem] text-neutral-800 bg-neutral-50 border border-neutral-200 rounded-lg p-3 overflow-x-auto whitespace-pre">{`[mcp_servers.report-generator]
url = "${mcpEndpoint}"

[mcp_servers.report-generator.http_headers]
Authorization = "Bearer <YOUR_TOKEN>"`}</pre>
    </div>
  </div>

  <p class="text-[0.68rem] text-neutral-500 leading-relaxed">
    Clients that only speak stdio and can't attach a custom header (e.g. Claude Desktop, whose
    remote connectors are OAuth-oriented) can bridge via
    <code class="font-mono">npx -y mcp-remote {mcpEndpoint} --header "Authorization: Bearer &lt;YOUR_TOKEN&gt;"</code>.
  </p>

  <p class="text-[0.68rem] text-neutral-500 leading-relaxed">
    Treat the token like a password — it grants row create/verify access. If you set
    <strong>Allowed IPs</strong> on a token, requests from other addresses are rejected, so make sure your
    client connects from a listed IP. Revoke a token above to cut access immediately.
  </p>
</section>
